/* eslint-disable jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions */

import React from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useLocation } from 'react-router-dom'
import { Popover, List, ListItem, ListItemText } from '@material-ui/core'
import { useTheme } from '@material-ui/core/styles'
import clsx from 'clsx'
import get from 'lodash/get'
import reduce from 'lodash/reduce'
import isEmpty from 'lodash/isEmpty'
import sortBy from 'lodash/sortBy'
import toPairs from 'lodash/toPairs'
import defaultTo from 'lodash/defaultTo'

import { Subco } from 'common/models/typings/Subco'
import { shortenString } from 'utils/shortenString'
import languages from './resource'
import Icon from '../.raw/Icon'
import DropdownMenu from '../.raw/DropdownMenu'

import Toggle from 'common/assets/images/sidebar/toggle.svg?react'
import ToggleCollapsed from 'common/assets/images/sidebar/toggle-collapsed.svg?react'
import Logo from 'common/assets/images/trunkrs-logo.svg'

import { useAppSelector } from 'planner-upgrades/redux/hooks'
import { getIsShipmentsFetching } from '../../../../selectors/ui'
import { getNetworkType } from 'selectors/networkType'
import { NETWORK_TYPE } from 'common/models/typings/TourOverview'

export interface SidebarProps {
  shipmentCount: number
  subcontractor: Subco
  subcontractors: Subco[]
  subcontractorIndexes: any[]
  collapsed?: boolean
  loading?: boolean
  currentLocale: string
  onSubcoClick: (ev: React.MouseEvent<any>) => void
  onToggleCollapsed: () => void
  onChangeSubco: (id: number) => void
  onLanguageChange: (lang: string) => void
  onLogoff: () => void
  onNetworkTypeChange: (networkType: NETWORK_TYPE) => void
}

const Sidebar: React.FC<SidebarProps> = ({
  shipmentCount,
  subcontractor,
  subcontractors,
  subcontractorIndexes,
  collapsed,
  currentLocale,
  onSubcoClick,
  onToggleCollapsed,
  onChangeSubco,
  onLanguageChange,
  onLogoff,
  loading,
  onNetworkTypeChange,
}) => {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const { t } = useTranslation()

  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null)
  const [showExpandedContent, setShowExpandedContent] =
    React.useState(!collapsed)

  const isShipmentsFetching = useAppSelector(getIsShipmentsFetching)
  const networkType = useAppSelector(getNetworkType)

  React.useEffect(() => {
    if (!collapsed) {
      const timer = setTimeout(() => {
        setShowExpandedContent(true)
      }, 100)
      return () => clearTimeout(timer)
    }
    return setShowExpandedContent(false)
  }, [collapsed])

  const handleToggleAnchor = React.useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      event.stopPropagation()
      if (subcontractors.length < 2) return
      setAnchorEl(anchorEl ? null : event.currentTarget)
    },
    [anchorEl, subcontractors.length],
  )

  const handleClickSubco = React.useCallback(
    (value: any) => {
      onChangeSubco(value)
      setAnchorEl(null)
    },
    [onChangeSubco],
  )

  const isPathActive = (path: string) => {
    if (path === '/') return pathname === path
    return pathname.includes(path)
  }

  const subcontractorsInitials = get(subcontractor, 'name', '')
    .split(' ')
    .map((str: string) => str.charAt(0))
    .join('')
    .substring(0, 2)

  const subcontractorsList = React.useMemo(
    () =>
      reduce(
        subcontractors,
        (acc, prop) => {
          ;(acc as any)[subcontractorIndexes[prop.tag as any]] = shortenString(
            `${prop.tag} - ${prop.name}`,
            20,
          )
          return acc
        },
        {},
      ),
    [subcontractorIndexes, subcontractors],
  )

  const languageList = React.useMemo(
    () =>
      reduce(
        languages,
        (acc, prop) => {
          ;(acc as any)[prop.value] = (
            <li className="">
              <span
                className={clsx(
                  'flex items-center w-full text-sm',
                  collapsed && 'ml-[-4px]',
                )}
              >
                {prop.icon}
                {collapsed ? null : <span className="ml-2">{prop.text}</span>}
              </span>
            </li>
          )
          return acc
        },
        {},
      ),
    [collapsed],
  )

  return (
    <div
      className={clsx(
        'fixed h-full bg-sidebar flex flex-col transition-all duration-300',
        collapsed ? 'w-24' : 'w-64',
      )}
    >
      <div className="flex flex-col h-full">
        <button
          type="button"
          className="absolute w-8 h-8 bg-green-50 text-white rounded-md top-8 -right-5 z-[9999] flex justify-center items-center shadow-elevation-2 hover:bg-green-60 transition-colors"
          onClick={onToggleCollapsed}
          style={{ boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.3)' }}
        >
          {collapsed ? <ToggleCollapsed /> : <Toggle />}
        </button>

        {/* Logo */}
        <div className={clsx(
          "relative h-20 flex items-center",
          collapsed ? "justify-center overflow-hidden" : ""
        )}>
          {collapsed ? (
            // Icon only when collapsed - show just the left portion of the SVG
            <div style={{ width: '48px', height: '48px', overflow: 'hidden' }}>
              <img
                src={Logo}
                alt="Trunkrs Logo"
                style={{
                  height: '48px',
                  width: 'auto',
                  filter: 'brightness(0) invert(1)',
                  marginLeft: '-2px'
                }}
              />
            </div>
          ) : (
            // Full logo when expanded
            <img
              src={Logo}
              alt="Trunkrs Logo"
              className="absolute left-8 top-8"
              style={{
                height: '25px',
                width: 'auto',
                filter: 'brightness(0) invert(1)'
              }}
            />
          )}
        </div>

        {/* Main Navigation */}
        <nav className="flex-1 overflow-auto mt-8">
          <ul className="">
            {/* All Shipments */}
            <li
              className={clsx(
                'flex items-center h-14 text-white cursor-pointer border-l-4 transition-colors',
                collapsed ? 'px-4 justify-center' : 'px-6',
                isPathActive('/')
                  ? 'bg-indigo-55 border-green-50'
                  : 'border-transparent hover:bg-purple-hover',
              )}
              onClick={() => navigate('/')}
            >
              <Icon
                name="package"
                fill="currentColor"
                className={clsx(
                  'w-6 h-6 flex-shrink-0',
                  isPathActive('/') ? 'text-green-50' : 'text-white',
                )}
              />
              {collapsed &&
                (isShipmentsFetching || loading ? (
                  <Icon className="w-5 h-5" name="spin-loader" />
                ) : (
                  <span className="absolute right-4 bg-red-50 text-white text-[8px] rounded-full px-1 py-1 min-w-[24px] text-center">
                    {shipmentCount}
                  </span>
                ))}

              {!collapsed && (
                <>
                  <span className="ml-4 flex-1 text-sm text-nowrap">
                    {t('ALL_SHIPMENTS')}
                  </span>
                  {isShipmentsFetching || loading ? (
                    <Icon className="w-5 h-5" name="spin-loader" />
                  ) : (
                    <span className="bg-red-50 text-white text-xs rounded-full px-2 py-1 min-w-[24px] text-center">
                      {shipmentCount}
                    </span>
                  )}
                </>
              )}
            </li>

            {/* Planning */}
            <li
              className={clsx(
                'flex items-center h-14 text-white cursor-pointer border-l-4 transition-colors',
                collapsed ? 'px-4 justify-center' : 'px-6',
                isPathActive('/planning')
                  ? 'bg-indigo-55 border-green-50'
                  : 'border-transparent hover:bg-purple-hover',
              )}
              onClick={() => navigate('/planning')}
            >
              <Icon
                name="organisation"
                fill="currentColor"
                className={clsx(
                  'w-6 h-6 flex-shrink-0',
                  isPathActive('/planning') ? 'text-green-50' : 'text-white',
                )}
              />
              {!collapsed && (
                <span className="ml-4 flex-1 text-sm">
                  {t('LOCK_PLANNING_TITLE')}
                </span>
              )}
            </li>
          </ul>
        </nav>

        {/* Bottom Section */}
        <div className="border-t border-white/10">
          {/* Subcontractor */}
          {subcontractor && !isEmpty(subcontractor) && (
            <div
              className={clsx(
                'flex items-center py-4 max-h-14 h-14 cursor-pointer hover:bg-purple-hover',
                collapsed ? 'px-4 justify-center' : 'px-4',
              )}
              onClick={onSubcoClick}
            >
              <div className="w-10 h-10 rounded-full bg-indigo-45 text-white text-sm flex items-center justify-center font-bold flex-shrink-0">
                {subcontractorsInitials}
              </div>
              {!collapsed && (
                <div className="ml-4 flex-1 min-w-0">
                  <div
                    className="flex items-center text-white text-sm hover:text-green-50"
                    onClick={
                      subcontractors.length > 1 ? handleToggleAnchor : undefined
                    }
                  >
                    <span className="truncate">
                      {defaultTo(
                        shortenString(subcontractor.name, 15),
                        get(
                          subcontractorsList,
                          subcontractorIndexes[
                            subcontractor.tag as unknown as number
                          ],
                          '',
                        ),
                      )}
                    </span>
                    {subcontractors.length > 1 && (
                      <Icon
                        name="caret-down"
                        fill="currentColor"
                        className="ml-1 w-4 h-4"
                      />
                    )}
                  </div>
                  <div className="text-white/60 text-xs">
                    {t('SUBCONTRACTOR')}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Subcontractor Popover */}
          <Popover
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            onClose={handleToggleAnchor}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            transformOrigin={{ vertical: 'top', horizontal: 'center' }}
            PaperProps={{
              className: 'p-0 overflow-y-auto min-w-[200px] max-w-[400px]',
            }}
          >
            <List disablePadding>
              {sortBy(toPairs(subcontractorsList), '[1]').map((pair) => {
                const keyValue = get(pair, '[0]')
                const text = get(pair, '[1]')
                return (
                  <ListItem
                    button
                    key={text as string}
                    onClick={() => handleClickSubco(keyValue)}
                  >
                    <ListItemText primary={text as string} />
                  </ListItem>
                )
              })}
            </List>
          </Popover>

          {/* Employees */}
          <div
            className={clsx(
              'flex items-center h-14 max-h-14 text-white cursor-pointer hover:bg-purple-hover',
              collapsed ? 'px-4 justify-center' : 'px-6',
              isPathActive('/drivers') && 'text-green-50',
            )}
            onClick={() => navigate('/drivers')}
          >
            <Icon
              name="user"
              fill="currentColor"
              className="w-5 h-5 flex-shrink-0"
            />
            {!collapsed && (
              <span className="ml-4 text-sm">{t('EMPLOYEES')}</span>
            )}
          </div>

          {/* Network selector */}
          {collapsed || !showExpandedContent ? (
            <div className="flex items-center h-14 px-8 text-white cursor-pointer transition-colors">
              {networkType === NETWORK_TYPE.DAY ? (
                <Icon name="sun" className="m-auto h-5 w-5 text-sm" />
              ) : (
                <Icon name="moon" className="m-auto h-5 w-5 text-sm" />
              )}
            </div>
          ) : (
            <div>
              <div className="flex gap-2 py-2 px-6">
                <label
                  htmlFor="day"
                  className={clsx(
                    'flex bg-white items-center gap-2 px-3 py-2 rounded-lg border-2 cursor-pointer transition-all flex-1',
                    networkType === NETWORK_TYPE.DAY
                      ? 'border-violet-50'
                      : 'border-white/20 hover:border-white/40',
                  )}
                >
                  <input
                    id="day"
                    type="radio"
                    checked={networkType === NETWORK_TYPE.DAY}
                    onChange={() => onNetworkTypeChange(NETWORK_TYPE.DAY)}
                    className="w-5 h-5 cursor-pointer accent-violet-50 flex-shrink-0"
                  />
                  <span className="text-indigo-50 text-sm whitespace-nowrap">
                    {t('NETWORK_TYPE.DAY')}
                  </span>
                </label>

                <label
                  htmlFor="night"
                  className={clsx(
                    'flex bg-white items-center gap-2 px-3 py-2 rounded-lg border-2 cursor-pointer transition-all flex-1',
                    networkType === NETWORK_TYPE.NIGHT
                      ? 'border-violet-50'
                      : 'border-white/20 hover:border-white/40',
                  )}
                >
                  <input
                    id="night"
                    type="radio"
                    checked={networkType === NETWORK_TYPE.NIGHT}
                    onChange={() => onNetworkTypeChange(NETWORK_TYPE.NIGHT)}
                    className="w-5 h-5 cursor-pointer accent-violet-50 flex-shrink-0"
                  />
                  <span className="text-indigo-50 text-sm whitespace-nowrap">
                    {t('NETWORK_TYPE.NIGHT')}
                  </span>
                </label>
              </div>
            </div>
          )}

          {/* Language Selector */}
          {collapsed ? (
            <div className="px-6 py-3">
              <DropdownMenu
                data={languageList}
                value={currentLocale || 'en'}
                onChange={onLanguageChange}
              />
            </div>
          ) : (
            <div className="px-6 py-3">
              <DropdownMenu
                data={languageList}
                value={currentLocale || 'en'}
                onChange={onLanguageChange}
              />
            </div>
          )}

          {/* Sign Out */}
          <div
            className={clsx(
              'flex items-center h-14 max-h-14 text-red-50 cursor-pointer hover:bg-purple-hover',
              collapsed ? 'px-4 justify-center' : 'px-6',
            )}
            onClick={onLogoff}
          >
            <Icon
              name="logout"
              fill="currentColor"
              className="w-5 h-5 flex-shrink-0"
            />
            {!collapsed && <span className="ml-4 text-sm">{t('LOG_OFF')}</span>}
          </div>
        </div>
      </div>
    </div>
  )
}

export default React.memo(Sidebar)
